// src/components/FieldError.tsx
//
// ★★ ONE field-level error, wired to its input the way a screen reader needs.
//
// ══ WHAT THIS IS FOR, AND — MORE IMPORTANTLY — WHAT IT IS NOT ══
//
// `docs/delivery/DEMO-READINESS-PLAN.md` §Wave 5 records that form validation is
// "presence-only across eleven admin screens: no field-level errors, no
// `pattern`, no `maxLength`, and `rankOrder` accepts `1.5` and `1e999`. The
// failure arrives as one opaque line under the form."
//
// Two of those four are now closed and the other two are DELIBERATELY NOT, which
// is the distinction this file exists to hold:
//
//   ✅ **The opaque line** — `FormResult` announces the form's outcome in a live
//      region (WCAG 2.2 3.3.1), so a refusal is heard rather than painted.
//   ✅ **A TYPE problem gets a FIELD-LEVEL error** — this component. "1.5 is not
//      a whole number" is a fact about the input, knowable here, and worth saying
//      beside the box rather than after a round trip.
//   ❌ **A POLICY bound stays on the SERVER.** `rankOrder >= 1`, a key's
//      `maxLength`, a syntax pattern — those are the route's rules
//      (`routes/ward-classes.ts`), and several are administered data
//      (`app.reference_parameter`'s own `validation` block). Restating them here
//      would be a SECOND copy that drifts, and this repository's S-011 findings
//      are all that shape. Where a screen legitimately shows a bound, it shows
//      the one the SERVER published — `ReferenceDataPage` does exactly that.
//   ❌ **`pattern`/`maxLength` attributes are not added blind.** An attribute
//      whose value this screen invented is the same second copy in HTML clothing.
//
// ⚠ So the rule for a caller: use this for what the BROWSER can know (is this a
// whole number, is this blank, is this a real date). Send everything else and let
// the route answer — its refusal lands in `FormResult`, out loud.
//
// ══ ★★ WAVE 8 (Task 3) — THE TWO ❌s ABOVE STILL HOLD, AND THE GAP THEY LEFT IS
//    NOW CLOSED FROM THE OTHER SIDE ══
//
// Read the two ❌ bullets again, because nothing in them has been relaxed: a
// policy bound still lives on the SERVER, and an attribute whose value a screen
// invented is still refused. What changed is that the SERVER now PUBLISHES those
// bounds for all thirteen administration catalogues — `validation` on each list
// payload, from `services/gateway/src/routes/administration-field-bounds.ts` —
// so a screen can show the real rule without ever holding a copy of it. That is
// the "where a screen legitimately shows a bound" case the third bullet already
// pointed at `ReferenceDataPage` for; it is now available everywhere.
//
// The rendering lives in `components/BoundedField.tsx` and the comparisons in
// `lib/published-bounds.ts`, which is where the reasons are written down. Both
// call THIS file's {@link fieldErrorProps} and {@link FieldError}, so there is
// still one wiring of `aria-invalid` + `aria-describedby` in this app. And
// `published-bounds.test.ts` is the census that keeps the count of screen-invented
// bounds at ZERO.
//
// ══ ★★ WHY A NUMERIC FIELD HERE IS `type="text"` AND NOT `type="number"` ══
//
// ⚠ **A `type="number"` input DISCARDS what the officer typed, and then every
// check in this file reasons about an empty string instead.** Measured in
// Chromium (`e2e/write-forms.spec.ts` is what found it):
//
//     el.value = '1e999'                  -> el.value reads ''
//     typing   '1e999'                    -> el.value reads '', validity.badInput TRUE
//                                            ...and the box still SHOWS "1e999"
//     el.value = 'abc'                    -> el.value reads ''
//     el.value = '1.5'                    -> survives (so a FRACTION does reach us)
//
// So on a number input the officer sees a filled box, React holds `''`,
// {@link wholeNumberProblem} answers `null` because "blank is `required`'s
// business", and the form arrives at a DISABLED submit button with NOTHING said.
// That is a worse WCAG 2.2 3.3.1 failure than the opaque line this file was
// written to remove, and it is INVISIBLE to a jsdom test: jsdom implements no
// value sanitisation, so the string goes straight through and the assertion
// passes.
//
// ⚠ The remedy is to let the raw text reach the parser: `type="text"` plus
// `inputMode="numeric"` (or `"decimal"`), which keeps the numeric keypad on a
// touch device without handing the browser a veto over what we are allowed to
// see. Both numeric admin fields now do that — `WardClassesPage`'s rank order
// and `CostEstimatePage`'s bill amount — and both carry the reason inline.
//
// ⚠ It also means `Number.isFinite(...)` guards written against a number input
// were DEAD in a browser: the browser had already made the value either a valid
// float string or empty. One such guard existed on the estimate form.

// ══ THE WIRING, AND WHY EACH PART IS REQUIRED ══
//
//   * **`aria-invalid`** on the input, so assistive technology reports the field
//     as invalid rather than merely reading an unrelated sentence after it.
//   * **`aria-describedby`** pointing at THIS element's id, so the message is
//     read as part of the field — the association is what turns a nearby
//     paragraph into an error message.
//   * **`role="alert"`** so it is announced when it appears mid-typing.
//
// ⚠ It renders NOTHING when there is no problem, and the input's `aria-invalid`
// must be driven by the same value — which is why {@link fieldErrorProps} exists:
// a caller that set one and forgot the other would have a field that announces an
// error nobody is told about, or an invalid field with no reason.
export interface FieldErrorProps {
  /** The id this element takes, and the one the input's `aria-describedby` must name. */
  readonly id: string
  /** The problem, or `null` when there is none. */
  readonly problem: string | null
}

export function FieldError({ id, problem }: FieldErrorProps) {
  if (problem === null) return null
  return (
    <span id={id} role="alert" className="text-xs text-[var(--sgds-danger-color-emphasis)]">
      {problem}
    </span>
  )
}

/**
 * The two attributes an input must carry for {@link FieldError} to mean anything.
 *
 * ⚠ Returned as a pair so they cannot be set independently. `aria-describedby` is
 * present ONLY while there is a problem: naming an element that does not exist is
 * a dangling reference some screen readers announce as an empty description,
 * which is worse than none.
 *
 * ★★ WAVE 8 (Task 3) — `alsoDescribedBy` exists because a field can now carry a
 * SECOND description alongside its error: the rule the SERVER published for it
 * (see `lib/published-bounds.ts`). `aria-describedby` is ONE attribute, so a
 * caller that set the hint id itself would have to overwrite whatever this
 * returned — which is exactly the "set one and forget the other" split this
 * function exists to make impossible. The ids are composed HERE instead, and the
 * order is deliberate: the rule is read first and the failure second, which is
 * the order the officer needs them in. `null`/`undefined` entries are dropped so
 * a caller may pass a conditional id without minting a dangling reference.
 */
export function fieldErrorProps(
  id: string,
  problem: string | null,
  alsoDescribedBy: readonly (string | null | undefined)[] = [],
): { 'aria-invalid': boolean; 'aria-describedby'?: string } {
  const ids = [...alsoDescribedBy, problem === null ? null : id].filter(
    (candidate): candidate is string => typeof candidate === 'string' && candidate.length > 0,
  )
  return {
    'aria-invalid': problem !== null,
    ...(ids.length > 0 && { 'aria-describedby': ids.join(' ') }),
  }
}

/**
 * ★ The one TYPE check the admin screens actually need: a whole number.
 *
 * ⚠ `Number('1e999')` is `Infinity`, and `JSON.stringify({ x: Infinity })`
 * produces `{"x":null}` — so an unchecked field sends `null` and the route answers
 * a message about a MISSING value for a field the officer plainly filled in. That
 * is the specific confusion `rankOrder accepts 1e999` produces, and it is why this
 * checks `Number.isSafeInteger` rather than `Number.isInteger`.
 *
 * ⚠ It does NOT check a minimum. `rankOrder >= 1` is the route's rule and stays
 * there — see this file's own header on why a second copy is refused.
 */
export function wholeNumberProblem(raw: string, fieldLabel: string): string | null {
  const trimmed = raw.trim()
  if (trimmed.length === 0) return null // blank is `required`'s business, not this
  if (!/^-?\d+$/.test(trimmed)) {
    return `${fieldLabel} must be a whole number: no decimal point, no exponent.`
  }
  if (!Number.isSafeInteger(Number(trimmed))) {
    return `${fieldLabel} is too large to send exactly. Use a smaller whole number.`
  }
  return null
}


/**
 * ★ Dollars-and-cents from what the officer typed, or the reason it cannot be.
 *
 * ⚠ **>2 decimal places is REFUSED, never rounded** — the same rule
 * `ReferenceDataPage`'s parser applies, and this is the one implementation of it
 * (that parser's `CENTS` branch calls this). A silently-rounded amount is a
 * different amount, and the officer is never told which one was sent.
 *
 * ⚠ The conversion is done on the DECIMAL STRING, not by `Number(x) * 100`:
 * `Number('200.10') * 100` is `20010.000000000004`, and a `Math.round` that
 * usually saves you is not a thing to rely on for money.
 */
export function parseCents(raw: string): { cents: number } | { error: string } {
  const trimmed = raw.trim()
  if (trimmed.length === 0) return { error: 'Enter an amount in dollars and cents, for example 128.50.' }
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    return {
      error:
        'Enter an amount in dollars and cents, for example 128.50. More than two decimal places is refused ' +
        'rather than rounded: a rounded amount is a different amount, quoted as if it were the one you typed.',
    }
  }
  const [dollars = '0', fraction = ''] = trimmed.split('.')
  const cents = Number(dollars) * 100 + Number(fraction.padEnd(2, '0'))
  if (!Number.isSafeInteger(cents)) return { error: 'That amount is too large to send exactly.' }
  return { cents }
}

/**
 * {@link parseCents} in the shape {@link FieldError} takes.
 *
 * ⚠ Blank answers `null` for the same reason {@link wholeNumberProblem} does: an
 * UNTOUCHED field is not an invalid one, and `role="alert"` would announce the
 * accusation the moment the form opened. Emptiness is `required`'s business, and
 * the caller must arm its button off the PARSER rather than off this.
 */
export function centsProblem(raw: string, fieldLabel: string): string | null {
  if (raw.trim().length === 0) return null
  const parsed = parseCents(raw)
  return 'error' in parsed ? `${fieldLabel}: ${parsed.error}` : null
}
